import { ContentType } from '@/graphql/graphql';
import { Link } from '@/i18n/navigation';
import { CategoryDropdownMenu } from '@/types/categories';
import { ChevronDown } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';

type Props = {
    isActive?: boolean;
    categories: CategoryDropdownMenu[];
    contentType?: ContentType;
    onLinkClickAction?: () => void;
};

export default function CategoriesDropDown({ isActive, categories, contentType, onLinkClickAction }: Props) {
    const t = useTranslations('Navigation');

    const [catMenuOpen, setCatMenuOpen] = useState<boolean>(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const listItemClassName: string = `flex rounded-[8px] p-[10px] my-2 relative text-base
                                        hover:bg-[rgba(255,255,255,0.30)] cursor-pointer
                                        after:content-[''] after:absolute
                                        after:-bottom-1 after:left-0
                                        after:w-full after:h-[1px]
                                        after:bg-[rgba(255,255,255,0.10)]`;

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                (event.target instanceof Element && event.target.matches('a')) ||
                (containerRef.current && event.target instanceof Node && !containerRef.current.contains(event.target))
            ) {
                setCatMenuOpen(false);
            }
        };

        if (catMenuOpen) document.addEventListener('click', handleClickOutside);

        return () => document.removeEventListener('click', handleClickOutside);
    }, [catMenuOpen]);

    const handleLinkClick = () => {
        setCatMenuOpen(false);
        onLinkClickAction?.();
    };

    return (
        <div ref={containerRef} className="relative">
            <button
                type="button"
                className={`
                        px-3 py-3 rounded-[8px] cursor-pointer flex
                        transition-colors text-white hover:text-gray-400 duration-300
                        ${isActive ? 'sm:bg-[rgba(255,255,255,0.30)]' : 'bg-[transparent]'}
                    `}
                onClick={() => setCatMenuOpen(!catMenuOpen)}
            >
                <span className="pr-[10px]">{t(contentType === ContentType.Format ? 'formats' : 'programs')}</span>
                <ChevronDown />
            </button>
            <ul
                className={`
                    px-[10px] pt-1 pb-[0.5px] absolute bg-[rgba(14,14,14,1)] rounded-[8px] z-1
                    w-[calc(100vw-32px)] sm:w-[205px]
                    md:w-auto
                    md:max-w-[300px]
                    lg:max-w-[400px]
                    lg:min-w-[200px]
                    max-h-[440px] sm:max-h-[initial]
                    overflow-y-auto
                    ${catMenuOpen ? 'block' : 'hidden'}
                `}
            >
                <li>
                    <Link
                        className={listItemClassName}
                        href={`/${contentType === ContentType.Format ? 'formats' : 'programs'}`}
                        onClick={handleLinkClick}
                    >
                        {t(contentType === ContentType.Format ? 'allFormats' : 'allPrograms')}
                    </Link>
                </li>
                {categories.map((category, index, array) => (
                    <li key={category.id}>
                        <Link
                            className={`whitespace-nowrap  ${listItemClassName} ${index === array.length - 1 ? 'last:after:content-[none]' : ''}`}
                            href={{
                                pathname: `/${contentType === ContentType.Format ? 'formats' : 'programs'}`,
                                query: { [category.isAnExcludedGenre ? 'gens' : 'category']: category.id },
                            }}
                            onClick={handleLinkClick}
                        >
                            {category.title as string}
                        </Link>
                    </li>
                ))}
            </ul>
        </div>
    );
}
